/** SUNTRONIX ONE — Commercial → AMC / PMS Automation. */
import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  FileSignature, CalendarClock, RefreshCw, Sparkles, PlusCircle, X, Zap,
  Ticket, Wrench, ChevronRight,
} from "lucide-react";

const din = "mt-1 w-full text-[12px] border border-slate-200 rounded-md px-2 py-1.5";
const TYPE_TINT = {
  AMC: "bg-teal-100 text-teal-800", WARRANTY_SUPPORT: "bg-blue-100 text-blue-800",
  PREVENTIVE_MAINTENANCE: "bg-emerald-100 text-emerald-800",
  MANAGED_SERVICE: "bg-violet-100 text-violet-800", SUBSCRIPTION: "bg-amber-100 text-amber-800",
  RETAINER: "bg-slate-100 text-slate-700",
};

export default function AmcPms() {
  const [tab, setTab] = useState("contracts");
  const [meta, setMeta] = useState(null);
  const [contracts, setContracts] = useState([]);
  const [renewals, setRenewals] = useState(null);
  const [visits, setVisits] = useState([]);
  const [openC, setOpenC] = useState(null);
  const [create, setCreate] = useState(false);
  const [ai, setAi] = useState({ q: "", res: null, loading: false });

  const load = useCallback(() => {
    api.get("/amc/meta").then(({ data }) => setMeta(data)).catch(() => {});
    api.get("/amc/contracts").then(({ data }) => setContracts(data.items || [])).catch(() => {});
    api.get("/amc/renewals").then(({ data }) => setRenewals(data)).catch(() => {});
    api.get("/amc/visits").then(({ data }) => setVisits(data.items || [])).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const runAuto = async () => {
    try { const { data } = await api.post("/amc/run-automation");
      toast.success(`Automation: ${data.tickets_created} tickets, ${data.field_jobs_created} jobs, ${data.renewals_created} renewals`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const askAi = async () => {
    if (!ai.q.trim()) return;
    setAi((p) => ({ ...p, loading: true, res: null }));
    try { const { data } = await api.post("/amc/ai", { query: ai.q });
      setAi((p) => ({ ...p, loading: false, res: data }));
    } catch (e) { setAi((p) => ({ ...p, loading: false })); toast.error("AI failed"); }
  };

  return (
    <div className="mx-auto max-w-[1200px] space-y-4" data-testid="amc-pms-page">
      <header className="flex flex-wrap items-center gap-3">
        <div className="grid h-10 w-10 place-items-center rounded-lg bg-teal-900 text-white"><FileSignature className="h-5 w-5" /></div>
        <div className="flex-1">
          <div className="font-heading text-xl font-semibold">AMC / PMS Automation</div>
          <div className="text-[11px] text-slate-500">Contracts · Preventive maintenance · Auto-ticketing · Renewal automation</div>
        </div>
        <button onClick={runAuto} data-testid="amc-run-automation"
          className="inline-flex items-center gap-1 rounded-md border border-teal-700 px-3 py-1.5 text-[11px] font-semibold text-teal-800">
          <Zap className="h-3.5 w-3.5" /> Run automation
        </button>
        {meta?.can_write && (
          <button onClick={() => setCreate(true)} data-testid="amc-new-contract"
            className="inline-flex items-center gap-1 rounded-md bg-teal-800 px-3 py-1.5 text-[11px] font-semibold text-white">
            <PlusCircle className="h-3.5 w-3.5" /> New contract
          </button>
        )}
      </header>

      <div className="flex gap-1 border-b border-slate-100 text-[12px]">
        {[["contracts", `Contracts (${contracts.length})`], ["visits", "PMS Visits"], ["renewals", "Renewals"], ["ai", "ONE AI"]].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`amc-tab-${k}`}
            className={`px-3 py-2 font-semibold border-b-2 -mb-px ${tab === k ? "border-teal-700 text-slate-900" : "border-transparent text-slate-500"}`}>{l}</button>
        ))}
      </div>

      {tab === "contracts" && (
        <div className="space-y-2" data-testid="amc-contracts-list">
          {contracts.map((c) => (
            <button key={c.id} onClick={() => setOpenC(c.id)} data-testid={`amc-contract-${c.id}`}
              className="flex w-full items-center gap-3 rounded-xl border border-slate-200 bg-white p-3 text-left hover:border-teal-400">
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-mono text-[10px] text-slate-400">{c.contract_number}</span>
                  <span className={`rounded px-1.5 py-0.5 text-[9px] font-semibold ${TYPE_TINT[c.contract_type] || "bg-slate-100"}`}>{c.contract_type?.replace(/_/g, " ")}</span>
                  <span className={`rounded px-1.5 py-0.5 text-[9px] font-semibold ${c.status === "ACTIVE" ? "bg-emerald-100 text-emerald-800" : "bg-slate-100 text-slate-600"}`}>{c.status}</span>
                </div>
                <div className="mt-1 truncate text-[13px] font-semibold text-slate-900">{c.title || c.customer_name || "—"}</div>
                <div className="mt-0.5 flex flex-wrap gap-2 text-[11px] text-slate-500">
                  {c.coverage && <span>{c.coverage}</span>}
                  <span>· {c.visit_frequency}</span>
                  <span>· ends {(c.end_date || "").slice(0, 10)}</span>
                  {c.days_to_expiry != null && (
                    <span className={c.days_to_expiry <= 90 ? "font-semibold text-amber-700" : ""}>· {c.days_to_expiry}d left</span>
                  )}
                </div>
              </div>
              <ChevronRight className="h-4 w-4 text-slate-300" />
            </button>
          ))}
          {!contracts.length && <div className="rounded-xl border border-dashed border-slate-200 p-8 text-center text-[12px] text-slate-400">No contracts yet.</div>}
        </div>
      )}

      {tab === "visits" && (
        <div className="rounded-xl border border-slate-200 bg-white" data-testid="amc-visits-list">
          <table className="w-full text-[11px]">
            <thead className="bg-slate-50 text-left text-[10px] uppercase text-slate-500">
              <tr><th className="px-3 py-2">Contract</th><th className="px-3 py-2">Customer</th><th className="px-3 py-2">Date</th><th className="px-3 py-2">Bucket</th><th className="px-3 py-2">Ticket</th><th className="px-3 py-2">Field job</th></tr>
            </thead>
            <tbody>
              {visits.map((v) => (
                <tr key={v.id} className="border-t border-slate-100" data-testid={`amc-visit-${v.id}`}>
                  <td className="px-3 py-1.5 font-mono text-[10px]">{v.contract_number}</td>
                  <td className="px-3 py-1.5">{v.customer_name || "—"}</td>
                  <td className="px-3 py-1.5">{(v.scheduled_date || "").slice(0, 10)}</td>
                  <td className="px-3 py-1.5">
                    <span className={`rounded px-1.5 py-0.5 text-[9px] font-semibold ${v.bucket === "overdue" ? "bg-rose-100 text-rose-700" : v.bucket === "completed" ? "bg-emerald-100 text-emerald-800" : "bg-blue-100 text-blue-800"}`}>{v.bucket}</span>
                  </td>
                  <td className="px-3 py-1.5">{v.ticket_number ? <span className="inline-flex items-center gap-1 text-teal-700"><Ticket className="h-3 w-3" />{v.ticket_number}</span> : "—"}</td>
                  <td className="px-3 py-1.5">{v.field_job_number ? <span className="inline-flex items-center gap-1 text-emerald-700"><Wrench className="h-3 w-3" />{v.field_job_number}</span> : "—"}</td>
                </tr>
              ))}
              {!visits.length && <tr><td colSpan={6} className="px-3 py-8 text-center text-slate-400">No visits scheduled.</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {tab === "renewals" && renewals && (
        <div className="space-y-3" data-testid="amc-renewals">
          <div className="grid grid-cols-3 gap-2 sm:grid-cols-6">
            {["d90", "d60", "d30", "d15", "d7", "expired"].map((k) => (
              <div key={k} className="rounded-xl border border-slate-200 bg-white p-2.5" data-testid={`amc-renewal-bucket-${k}`}>
                <div className="text-lg font-semibold text-slate-900">{renewals.counts?.[k] ?? 0}</div>
                <div className="text-[9.5px] uppercase text-slate-500">{k === "expired" ? "Expired" : `≤ ${k.slice(1)} days`}</div>
              </div>
            ))}
          </div>
          <div className="rounded-xl border border-slate-200 bg-white">
            <div className="border-b border-slate-100 px-3 py-2 text-[12px] font-semibold">Renewal pipeline ({(renewals.renewals || []).length})</div>
            {(renewals.renewals || []).map((r) => (
              <div key={r.id} className="flex items-center gap-2 border-t border-slate-100 px-3 py-1.5 text-[11px]">
                <span className="font-mono text-[10px] text-slate-400">{r.contract_number}</span>
                <span className="font-semibold">{r.customer_name}</span>
                <span className="text-slate-500">{r.contract_type}</span>
                <span className="ml-auto rounded bg-amber-100 px-1.5 py-0.5 text-[9px] font-semibold text-amber-800">{r.status}</span>
                <span className="text-slate-500">exp {(r.expiry_date || "").slice(0, 10)}</span>
              </div>
            ))}
            {!(renewals.renewals || []).length && <div className="px-3 py-6 text-center text-[11px] text-slate-400">No renewal opportunities yet. Run automation to generate them.</div>}
          </div>
        </div>
      )}

      {tab === "ai" && (
        <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="amc-ai">
          <div className="mb-2 flex items-center gap-2 text-[12px] font-semibold"><Sparkles className="h-4 w-4 text-violet-600" /> Ask ONE AI</div>
          <div className="flex flex-wrap gap-1.5">
            {["Which AMCs expire next month?", "Which PMS visits are overdue?", "Generate renewal list"].map((s) => (
              <button key={s} onClick={() => setAi((p) => ({ ...p, q: s }))} className="rounded-md border border-slate-200 px-2 py-1 text-[10px] text-slate-600 hover:bg-slate-50">{s}</button>
            ))}
          </div>
          <div className="mt-2 flex gap-2">
            <input value={ai.q} onChange={(e) => setAi((p) => ({ ...p, q: e.target.value }))} onKeyDown={(e) => e.key === "Enter" && askAi()}
              className={din + " flex-1"} placeholder="Ask about contracts / renewals / PMS…" data-testid="amc-ai-input" />
            <button onClick={askAi} data-testid="amc-ai-ask" className="rounded-md bg-violet-600 px-3 py-1.5 text-[11px] font-semibold text-white">Ask</button>
          </div>
          {ai.loading && <div className="mt-3 text-[11px] text-slate-500">Thinking…</div>}
          {ai.res && (
            <div className="mt-3 rounded-md border border-violet-200 bg-violet-50 p-3 text-[12px]" data-testid="amc-ai-answer">
              <div className="font-semibold text-slate-800">{ai.res.answer}</div>
              {(ai.res.items || []).slice(0, 30).map((it, i) => (
                <div key={i} className="mt-1 border-t border-violet-100 pt-1 text-[11px] text-slate-600">
                  {it.contract} · {it.customer} {it.days_to_expiry != null && `· ${it.days_to_expiry}d`} {it.scheduled_date && `· ${it.scheduled_date}`}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {openC && <ContractDetail cid={openC} canWrite={meta?.can_write} onClose={() => setOpenC(null)} onChange={load} />}
      {create && <CreateDialog meta={meta} onClose={() => setCreate(false)} onSaved={() => { setCreate(false); load(); }} />}
    </div>
  );
}

function ContractDetail({ cid, canWrite, onClose, onChange }) {
  const [data, setData] = useState(null);
  const load = useCallback(() => { api.get(`/amc/contracts/${cid}`).then(({ data }) => setData(data)).catch(() => {}); }, [cid]);
  useEffect(() => { load(); }, [load]);
  if (!data) return null;
  const c = data.contract;
  const gen = async () => { const { data: r } = await api.post(`/amc/contracts/${cid}/generate-schedule`); toast.success(`Schedule: ${r.total_visits} visits`); load(); onChange?.(); };
  const mkTicket = async (vid) => { try { const { data: r } = await api.post(`/amc/visits/${vid}/create-ticket`); toast.success(`Ticket ${r.ticket_number}${r.field_job_number ? " + job " + r.field_job_number : ""}`); load(); onChange?.(); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } };
  const mkRenewal = async () => { try { await api.post(`/amc/contracts/${cid}/create-renewal`); toast.success("Renewal opportunity created"); load(); onChange?.(); } catch { toast.error("Failed"); } };
  const complete = async (vid) => { await api.patch(`/amc/visits/${vid}`, { status: "COMPLETED" }); load(); onChange?.(); };
  return (
    <div className="fixed inset-0 z-[120] flex items-start justify-center bg-slate-900/50 p-0 sm:p-4 sm:pt-8" onClick={onClose}>
      <div className="flex h-full w-full max-w-2xl flex-col overflow-hidden bg-white sm:h-auto sm:max-h-[92vh] sm:rounded-2xl shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="amc-contract-detail">
        <div className="flex items-center gap-2 bg-teal-900 px-4 py-3 text-white">
          <div className="min-w-0 flex-1">
            <div className="font-mono text-[10px] text-teal-200">{c.contract_number} · {c.contract_type?.replace(/_/g, " ")}</div>
            <div className="truncate text-[14px] font-semibold">{c.title || c.customer_name}</div>
          </div>
          <span className="rounded bg-white/15 px-2 py-0.5 text-[10px] font-semibold">{c.status}</span>
          <button onClick={onClose} data-testid="amc-detail-close"><X className="h-4 w-4" /></button>
        </div>
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          <div className="grid grid-cols-2 gap-2 text-[11px]">
            <Info k="Customer" v={c.customer_name} /><Info k="Coverage" v={c.coverage} />
            <Info k="Start" v={(c.start_date || "").slice(0, 10)} /><Info k="Renewal date" v={(c.end_date || "").slice(0, 10)} />
            <Info k="Frequency" v={c.visit_frequency} /><Info k="SLA" v={c.sla} />
            <Info k="Value" v={c.commercial_value ? `${c.currency} ${c.commercial_value}` : "—"} />
            <Info k="Days to expiry" v={c.days_to_expiry} />
            <Info k="Services" v={(c.services_included || []).join(", ")} />
            <Info k="Exclusions" v={(c.exclusions || []).join(", ")} />
          </div>
          <div className="flex gap-2">
            {canWrite && <button onClick={gen} data-testid="amc-generate-schedule" className="rounded-md border border-teal-700 px-3 py-1.5 text-[11px] font-semibold text-teal-800">Generate PMS schedule</button>}
            {canWrite && <button onClick={mkRenewal} data-testid="amc-create-renewal" className="rounded-md border border-amber-500 px-3 py-1.5 text-[11px] font-semibold text-amber-700">Create renewal</button>}
          </div>
          <div className="rounded-lg border border-slate-200">
            <div className="border-b border-slate-100 px-3 py-2 text-[12px] font-semibold">PMS Visits ({data.visits.length})</div>
            {data.visits.map((v) => (
              <div key={v.id} className="flex items-center gap-2 border-t border-slate-100 px-3 py-1.5 text-[11px]" data-testid={`amc-detail-visit-${v.id}`}>
                <span className="text-slate-500">#{v.sequence}</span>
                <span>{(v.scheduled_date || "").slice(0, 10)}</span>
                <span className={`rounded px-1.5 py-0.5 text-[9px] font-semibold ${v.status === "COMPLETED" ? "bg-emerald-100 text-emerald-800" : v.status === "SCHEDULED" ? "bg-blue-100 text-blue-800" : "bg-slate-100 text-slate-600"}`}>{v.status}</span>
                {v.ticket_number && <span className="text-teal-700">{v.ticket_number}</span>}
                {v.field_job_number && <span className="text-emerald-700">{v.field_job_number}</span>}
                {canWrite && (
                  <span className="ml-auto flex gap-1.5">
                    {!v.ticket_id && <button onClick={() => mkTicket(v.id)} data-testid={`amc-visit-ticket-${v.id}`} className="rounded border border-teal-600 px-1.5 py-0.5 text-[9px] font-semibold text-teal-700">+ Ticket</button>}
                    {v.status !== "COMPLETED" && <button onClick={() => complete(v.id)} className="rounded border border-emerald-600 px-1.5 py-0.5 text-[9px] font-semibold text-emerald-700">Complete</button>}
                  </span>
                )}
              </div>
            ))}
            {!data.visits.length && <div className="px-3 py-6 text-center text-[11px] text-slate-400">No visits. Generate the schedule.</div>}
          </div>
        </div>
      </div>
    </div>
  );
}
const Info = ({ k, v }) => (<div className="rounded-md bg-slate-50 px-2 py-1.5"><div className="text-[9px] uppercase text-slate-400">{k}</div><div className="font-semibold text-slate-800">{v ?? "—"}</div></div>);

function CreateDialog({ meta, onClose, onSaved }) {
  const [f, setF] = useState({ contract_type: "AMC", title: "", customer_name: "", coverage: "",
    start_date: "", end_date: "", visit_frequency: "QUARTERLY", custom_interval_days: "",
    sla: "", services_included: "", exclusions: "", commercial_value: "", currency: "INR",
    renewal_terms: "", auto_ticket_lead_days: 7, auto_create_field_job: false, contact_name: "", contact_phone: "" });
  const save = async () => {
    if (!f.start_date || !f.end_date) return toast.error("Start & end dates required");
    try {
      await api.post("/amc/contracts", {
        ...f,
        custom_interval_days: f.custom_interval_days ? Number(f.custom_interval_days) : null,
        commercial_value: f.commercial_value ? Number(f.commercial_value) : null,
        auto_ticket_lead_days: Number(f.auto_ticket_lead_days) || 7,
        services_included: f.services_included.split(",").map((s) => s.trim()).filter(Boolean),
        exclusions: f.exclusions.split(",").map((s) => s.trim()).filter(Boolean),
      });
      toast.success("Contract created"); onSaved();
    } catch (e) { toast.error(JSON.stringify(e.response?.data?.detail) || "Failed"); }
  };
  return (
    <div className="fixed inset-0 z-[120] flex items-start justify-center overflow-y-auto bg-slate-900/40 p-4 pt-8" onClick={onClose}>
      <div className="w-full max-w-lg rounded-2xl bg-white p-4 shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="amc-create-dialog">
        <div className="mb-3 text-[13px] font-semibold">New AMC / PMS contract</div>
        <div className="grid grid-cols-2 gap-2">
          <label className="text-[10px] text-slate-500">Type
            <select value={f.contract_type} onChange={(e) => setF({ ...f, contract_type: e.target.value })} className={din} data-testid="amc-type">
              {(meta?.contract_types || []).map((t) => <option key={t}>{t}</option>)}
            </select></label>
          <label className="text-[10px] text-slate-500">Frequency
            <select value={f.visit_frequency} onChange={(e) => setF({ ...f, visit_frequency: e.target.value })} className={din} data-testid="amc-freq">
              {(meta?.frequencies || []).map((t) => <option key={t}>{t}</option>)}
            </select></label>
          <label className="col-span-2 text-[10px] text-slate-500">Title
            <input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} className={din} /></label>
          <label className="text-[10px] text-slate-500">Customer
            <input value={f.customer_name} onChange={(e) => setF({ ...f, customer_name: e.target.value })} className={din} data-testid="amc-customer" /></label>
          <label className="text-[10px] text-slate-500">Coverage
            <input value={f.coverage} onChange={(e) => setF({ ...f, coverage: e.target.value })} className={din} /></label>
          <label className="text-[10px] text-slate-500">Start date
            <input type="date" value={f.start_date} onChange={(e) => setF({ ...f, start_date: e.target.value })} className={din} data-testid="amc-start" /></label>
          <label className="text-[10px] text-slate-500">End date
            <input type="date" value={f.end_date} onChange={(e) => setF({ ...f, end_date: e.target.value })} className={din} data-testid="amc-end" /></label>
          {f.visit_frequency === "CUSTOM" && (
            <label className="text-[10px] text-slate-500">Custom interval (days)
              <input type="number" value={f.custom_interval_days} onChange={(e) => setF({ ...f, custom_interval_days: e.target.value })} className={din} /></label>
          )}
          <label className="text-[10px] text-slate-500">SLA
            <input value={f.sla} onChange={(e) => setF({ ...f, sla: e.target.value })} className={din} /></label>
          <label className="text-[10px] text-slate-500">Commercial value
            <input type="number" value={f.commercial_value} onChange={(e) => setF({ ...f, commercial_value: e.target.value })} className={din} /></label>
          <label className="text-[10px] text-slate-500">Auto-ticket lead (days)
            <input type="number" value={f.auto_ticket_lead_days} onChange={(e) => setF({ ...f, auto_ticket_lead_days: e.target.value })} className={din} /></label>
          <label className="col-span-2 text-[10px] text-slate-500">Services included (comma-sep)
            <input value={f.services_included} onChange={(e) => setF({ ...f, services_included: e.target.value })} className={din} /></label>
          <label className="col-span-2 text-[10px] text-slate-500">Exclusions (comma-sep)
            <input value={f.exclusions} onChange={(e) => setF({ ...f, exclusions: e.target.value })} className={din} /></label>
          <label className="col-span-2 text-[10px] text-slate-500">Renewal terms
            <input value={f.renewal_terms} onChange={(e) => setF({ ...f, renewal_terms: e.target.value })} className={din} /></label>
          <label className="col-span-2 flex items-center gap-2 text-[11px] text-slate-600">
            <input type="checkbox" checked={f.auto_create_field_job} onChange={(e) => setF({ ...f, auto_create_field_job: e.target.checked })} data-testid="amc-auto-fieldjob" />
            Auto-create a Field Service job for each PMS visit
          </label>
        </div>
        <div className="mt-3 flex justify-end gap-2">
          <button onClick={onClose} className="rounded-md border border-slate-200 px-3 py-1.5 text-[11px]">Cancel</button>
          <button onClick={save} data-testid="amc-create-save" className="rounded-md bg-teal-800 px-3 py-1.5 text-[11px] font-semibold text-white">Create contract</button>
        </div>
      </div>
    </div>
  );
}
